import { XIcon } from '@phosphor-icons/react';
import { Button } from '../../../Utility/Button';
import { Prompt } from '../../../../Types';
import Checkbox from '../../../Utility/FormElements/Checkbox';
import React, { useEffect, useRef, useState } from 'react';
import RadioButton from '../../../Utility/FormElements/Radiobutton';
import { Badge } from '../../../Utility/Badge';
import TabHeadline from './TabHeadline';
import InfoButton from '../../../Utility/InfoButton';
import { RootState, useAppSelector } from '../../../../store';
import { modelService } from '../../../../Services/modelService';
import { ArrowDownIcon } from '@phosphor-icons/react';

interface FunctionsTabProps {
    prompt: Prompt;
    i18n: any;
    PromptActions: any;
    filesInputKey: any;
    mimeTypes: string[];
    mimeTypesWithExtensions?: { [key: string]: string[] };
}

function FunctionsTab({
    prompt,
    i18n,
    PromptActions,
    filesInputKey,
    mimeTypes,
    mimeTypesWithExtensions,
}: FunctionsTabProps) {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const settings = useAppSelector((state: RootState) => state.settings);
    const userSettings = useAppSelector((state: RootState) => state.userSettings);
    const [isOpenAiModel, setIsOpenAiModel] = useState(false);
    const [isExpertExpanded, setIsExpertExpanded] = useState(false);
    const openaiDocumentProcessingEnabled = !!settings.openaiDocumentProcessingEnabled;
    const expertViewEnabled = !!userSettings.expertViewEnabled;

    useEffect(() => {
        if (!prompt.model) {
            setIsOpenAiModel(false);
            return;
        }
        modelService.getModel(prompt.model).then((response) => {
            const provider = response.data?.provider;
            setIsOpenAiModel(provider === 'openai-responses');
        });
    }, [prompt.model]);

    const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const resetFileInput = () => {
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        };
        PromptActions.handleFileChange(e, resetFileInput);
    };

    return (
        <div className="w-full">
            <TabHeadline
                title={i18n.translate({
                    id: 'tabs.functions.title',
                    sourceName: 'Prompt',
                })}
            />
            <InfoButton configKey="functions" />

            <div className="flex flex-col gap-6 mt-6">
                <Checkbox
                    key="chat_upload_enabled"
                    checked={prompt.chatUploadEnabled || false}
                    name="chat_upload_enabled"
                    onChange={PromptActions.handleChatUploadEnabledChange}
                    label={i18n.translate({
                        id: 'chatUploadEnabled',
                        sourceName: 'Prompt',
                    })}
                />

                <div>
                    <h3 className="text-lg font-medium mb-2">
                        {i18n.translate({ id: 'tabs.functions.section.files.title', sourceName: 'Prompt' })}
                    </h3>
                    <input
                        ref={fileInputRef}
                        type="file"
                        id="filesInput"
                        className="hidden"
                        multiple
                        onChange={handleFileInputChange}
                        key={filesInputKey}
                        accept={
                            mimeTypesWithExtensions && Object.keys(mimeTypesWithExtensions).length > 0
                                ? [
                                      ...Object.values(mimeTypesWithExtensions)
                                          .flat()
                                          .map((ext) => '.' + ext),
                                      ...Object.keys(mimeTypesWithExtensions),
                                  ].join(',')
                                : (mimeTypes ?? []).join(',')
                        }
                        title={i18n.translate({ id: 'filesUpload.button', sourceName: 'Prompt' })}
                    />

                    {prompt.files?.length > 0 && (
                        <ul className="mt-0">
                            {prompt.files?.map((file: any, index: any) => (
                                <li className="my-1" key={index}>
                                    {file.name}
                                    <span
                                        className="ml-1 hover:cursor-pointer"
                                        onClick={() => PromptActions.removeFile(index)}
                                    >
                                        <XIcon className="inline text-primary " />
                                    </span>
                                </li>
                            ))}
                        </ul>
                    )}

                    <Button
                        variant="secondary"
                        className="w-full max-w-[200px] mb-4"
                        onClick={() => PromptActions.handleFilesButtonClick()}
                    >
                        {i18n.translate({ id: 'filesUpload.button', sourceName: 'Prompt' })}
                    </Button>
                </div>

                {openaiDocumentProcessingEnabled && expertViewEnabled && (
                    <div>
                        <button
                            onClick={() => setIsExpertExpanded((prev) => !prev)}
                            className="w-full flex items-center py-4 text-left"
                            type="button"
                            title={i18n.translate({ id: 'expertSettings.title', sourceName: 'Prompt' })}
                            aria-label={i18n.translate({ id: 'expertSettings.title', sourceName: 'Prompt' })}
                        >
                            <span className="mr-3 text-gray-500 flex justify-center">
                                <span
                                    className="rounded-full h-[25px] md:h-[30px] w-[25px] md:w-[30px] transition-all duration-100 text-chatInput-sendButtonColor bg-chatInput-sendButtonBackground hover:bg-chatInput-sendButtonBackground-hover border-none flex items-center justify-center"
                                    aria-hidden="true"
                                >
                                    <ArrowDownIcon
                                        size={20}
                                        style={{
                                            transform: isExpertExpanded ? 'rotate(-180deg)' : 'rotate(0deg)',
                                            transition: 'transform 0.3s ease',
                                        }}
                                    />
                                </span>
                            </span>
                            <span className="text-layout-textcolor flex-1 font-medium">
                                {i18n.translate({ id: 'expertSettings.title', sourceName: 'Prompt' })}
                            </span>
                        </button>

                        <div className={`${isExpertExpanded ? 'h-fit' : 'h-0'} overflow-hidden`}>
                            <div className="pb-4 flex flex-col gap-4">
                                <p className="text-sm text-layout-textcolor opacity-80">
                                    {i18n.translate({ id: 'expertSettings.description', sourceName: 'Prompt' })}
                                </p>

                                {!isOpenAiModel ? (
                                    <Badge variant="info">
                                        {i18n.translate({ id: 'expertSettings.disabledHint', sourceName: 'Prompt' })}
                                    </Badge>
                                ) : null}

                                <div className={`${!isOpenAiModel ? 'opacity-50' : ''}`}>
                                    <div className="flex flex-col gap-2">
                                        <div className="font-medium text-layout-textcolor">
                                            {i18n.translate({
                                                id: 'expertSettings.documentProcessing.title',
                                                sourceName: 'Prompt',
                                            })}
                                        </div>
                                        <RadioButton
                                            name="documentProcessingMode"
                                            value="openai"
                                            checked={
                                                isOpenAiModel &&
                                                (prompt.documentProcessingMode ?? 'openai') === 'openai'
                                            }
                                            disabled={!isOpenAiModel}
                                            onChange={(e) => {
                                                PromptActions.updatePrompt({
                                                    documentProcessingMode: e.target.value,
                                                });
                                            }}
                                            label={i18n.translate({
                                                id: 'expertSettings.documentProcessing.openai',
                                                sourceName: 'Prompt',
                                            })}
                                        />
                                        <RadioButton
                                            name="documentProcessingMode"
                                            value="local"
                                            checked={!isOpenAiModel || prompt.documentProcessingMode === 'local'}
                                            disabled={!isOpenAiModel}
                                            onChange={(e) => {
                                                PromptActions.updatePrompt({
                                                    documentProcessingMode: e.target.value,
                                                    openAiCodeInterpreterEnabled: false,
                                                });
                                            }}
                                            label={i18n.translate({
                                                id: 'expertSettings.documentProcessing.local',
                                                sourceName: 'Prompt',
                                            })}
                                        />
                                    </div>

                                    <div className="mt-3">
                                        <Checkbox
                                            key="openai_code_interpreter"
                                            checked={prompt.openAiCodeInterpreterEnabled || false}
                                            name="openai_code_interpreter"
                                            disabled={!isOpenAiModel || prompt.documentProcessingMode === 'local'}
                                            onChange={(e) =>
                                                PromptActions.updatePrompt({
                                                    openAiCodeInterpreterEnabled: e.target.checked,
                                                })
                                            }
                                            label={i18n.translate({
                                                id: 'expertSettings.codeInterpreter',
                                                sourceName: 'Prompt',
                                            })}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default FunctionsTab;
